import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import memberService from '@/services/member.service';
import { MemberProfile, UpdateMemberProfileDto } from '@/types/member.types';
import { useDispatch } from 'react-redux';
import { setCredentials } from '@/store/authSlice';

/* Fields the app reads off the stored `user` blob rather than off the
   profile query — `crmId` is what Stripe knows the member by, and
   checkout, booking payment and the card-update screen all dead-end
   without it. Login writes them; nothing else may drop them. */
const IDENTITY_KEYS = [
  'crmId',
  'locationId',
  'franchiseId',
  'status',
  'roleId',
] as const;

const readStoredUser = (): Record<string, any> => {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

export const useMember = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const {
    data: memberProfile,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery<MemberProfile>({
    queryKey: ['memberProfile'],
    queryFn: () => memberService.getMemberProfile(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  /* Heal a user blob that an older build truncated. The profile is
     authoritative and carries every one of these fields, so anything
     missing locally is restored the next time this hook runs — no
     re-login needed. Only fills gaps, so a login-written value is
     never overwritten, and no-ops once there is nothing to add. */
  useEffect(() => {
    if (!memberProfile) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const stored = readStoredUser();
    const missing = IDENTITY_KEYS.filter(
      (key) => stored[key] == null && memberProfile[key] != null
    );
    if (missing.length === 0) return;

    const healed = { ...stored };
    missing.forEach((key) => {
      healed[key] = memberProfile[key];
    });

    localStorage.setItem('user', JSON.stringify(healed));
    dispatch(setCredentials({ token, member: healed }));
  }, [memberProfile, dispatch]);

  const updateProfileMutation = useMutation({
    mutationFn: (data: UpdateMemberProfileDto) => 
      memberService.updateMemberProfile(data),
    onSuccess: (updatedProfile) => {
      queryClient.setQueryData(['memberProfile'], updatedProfile);
      
      // Update Redux store with new user data.
      // MERGE, never replace: this used to write only the six fields
      // below, which silently dropped crmId/locationId/franchiseId from
      // the stored user and left that device unable to reach the payment
      // screen until the member logged out and back in.
      const token = localStorage.getItem('token');
      if (token) {
        const merged = {
          ...readStoredUser(),
          id: updatedProfile.id,
          firstName: updatedProfile.firstName,
          lastName: updatedProfile.lastName,
          email: updatedProfile.email,
          mobile: updatedProfile.mobile,
          avatar: updatedProfile.avatar,
        };

        dispatch(setCredentials({ token, member: merged }));
        localStorage.setItem('user', JSON.stringify(merged));
      }
    },
  });

  const uploadAvatarMutation = useMutation({
    mutationFn: (file: File) => memberService.uploadAvatar(file),
    // REFETCH, DON'T TRUST THE RESPONSE. The upload endpoint returns the
    // member with `avatar` set to the raw S3 key ("avatars/<id>/<ts>.jpg"),
    // not a URL — only GET /member-profile signs it. Writing the response
    // into the cache would put that key straight into an <img src>.
    onSuccess: async () => {
      const { data: updatedProfile } = await refetch();
      if (!updatedProfile) return;

      // Then mirror it into Redux and localStorage, the way updating the
      // profile and removing the avatar already do. Without this, every
      // surface reading `state.auth.user.avatar` (SAvatar, and the stored
      // user a cold start rehydrates from) kept the OLD photo after an
      // upload — the new one appeared only on the screen that did the
      // uploading, until a full reload.
      const token = localStorage.getItem('token');
      if (!token) return;

      const userStr = localStorage.getItem('user');
      const currentUser = userStr ? JSON.parse(userStr) : {};
      const updatedUser = {
        ...currentUser,
        id: updatedProfile.id,
        avatar: updatedProfile.avatar,
      };

      dispatch(setCredentials({ token, member: updatedUser }));
      localStorage.setItem('user', JSON.stringify(updatedUser));
    },
  });

  const removeAvatarMutation = useMutation({
    mutationFn: () => memberService.removeAvatar(),
    onSuccess: (updatedProfile) => {
      queryClient.setQueryData(['memberProfile'], updatedProfile);
      
      // Update Redux store and localStorage with avatar set to null
      const token = localStorage.getItem('token');
      if (token) {
        const userStr = localStorage.getItem('user');
        const currentUser = userStr ? JSON.parse(userStr) : {};
        
        const updatedUser = {
          ...currentUser,
          avatar: null, // Just set avatar to null
        };
        
        dispatch(setCredentials({ 
          token, 
          member: updatedUser
        }));
        
        // Update localStorage with avatar set to null
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    },
  });

  return {
    memberProfile,
    isLoading,
    /* True during a retry as well as the first load — `isLoading` is
       false once a query has failed, so anything waiting on the
       profile to arrive has to watch this instead. */
    isFetching,
    error,
    refetch,
    updateProfile: updateProfileMutation.mutate,
    isUpdating: updateProfileMutation.isPending,
    updateError: updateProfileMutation.error,
    uploadAvatar: uploadAvatarMutation.mutate,
    isUploadingAvatar: uploadAvatarMutation.isPending,
    removeAvatar: removeAvatarMutation.mutate,
    isRemovingAvatar: removeAvatarMutation.isPending,
  };
};
